import { useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { Link, useSearchParams } from 'react-router';
import { useMaterialReactTable, MaterialReactTable } from 'material-react-table';
import { IconButton, Button, Box, Tooltip } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';
import { removeAlertAC, updateAlertAC } from '../../redux/actions/dataAlerts';
import BuildingsLoader from '../loaders/BuildingsLoader';
import EquipmentLoader from '../loaders/EquipmentLoader';
import DataAlertsLoader from '../loaders/DataAlertsLoader';
import { validateEmails } from '../../utils/parsing';
import {
  CheckboxCell,
  CheckboxFilter,
  DateCell,
  CellList,
  DurationCell,
  ListTextEditCell,
  TextEditCell,
  NumberEditCell,
} from '../util/DataTableCells';
import {
  defaultTableOptions,
  defaultTableInitialState,
  extractSortingParams,
  tableCellularEditingOptions,
  extractFilterParams,
  sortingUpdater,
  filterUpdater,
} from '../util/data-table';
import { ONLINE, ALERT } from '../explore/explore';
import AlertState from './AlertState';
import { unique } from '../../utils/collections';

const AlertSegmentList = ({ row, renderedCellValue }) => {
  const renderValue = () => renderedCellValue;
  return (
    <>
      {row.original.equipment ? 'Equipment' : 'Buildings'}: <CellList cell={{ renderValue }} />
    </>
  );
};

const AlertLinkCell = ({ row, renderedCellValue }) => {
  let link;
  const filteredStatus = renderedCellValue === 'OKAY' ? ONLINE : ALERT;
  if (row.original.buildings)
    link = `/explore/buildings?filterBy=${JSON.stringify({ b_status: filteredStatus })}`;
  else
    link = `/explore/equipment?filterBy=${JSON.stringify({ e_status: filteredStatus })}&bids=${JSON.stringify(
      Object.fromEntries(
        row.original.equipment
          ?.map((e) => e.building_id)
          .filter(Boolean)
          .map((bid) => [bid, true]) ?? []
      )
    )}`;

  return (
    <Link to={link}>
      <Button variant="contained" color={renderedCellValue === 'OKAY' ? 'success' : 'warning'}>
        <AlertState state={renderedCellValue} showLabel={false} />
      </Button>
    </Link>
  );
};

const DataAlerts = ({
  alerts,
  buildingsById,
  equipmentById,
  alertsLoaded,
  orgId,

  removeAlert,
  updateAlert,
}) => {
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const sorting = useMemo(() => extractSortingParams(searchParams), [searchParams]);
  const columnFilters = useMemo(() => extractFilterParams(searchParams), [searchParams]);
  const onSortingChange = useCallback(
    (updater) => setSearchParams(sortingUpdater(sorting)(updater)),
    [setSearchParams, sorting]
  );
  const onColumnFiltersChange = useCallback(
    (updater) => setSearchParams(filterUpdater(columnFilters)(updater)),
    [setSearchParams, columnFilters]
  );

  const columns = useMemo(
    () => [
      {
        header: 'Alert Name',
        accessorKey: 'name',
        enableEditing: true,
        // eslint-disable-next-line react/no-unstable-nested-components
        Edit: ({ row }) => (
          // Restructure TextEditCell to take in the full props and an additional property for the keyand update function?
          <TextEditCell
            value={row.original.name}
            update={(newValue) => updateAlert(row.original.id, { name: newValue })}
            validator={(n) => (n === '' ? 'Name must be present' : undefined)}
          />
        ),
      },
      {
        header: 'Alert Active',
        accessorKey: 'enabled',
        enableEditing: false,
        onChange: (event, row) => updateAlert(row.original.id, { enabled: event.target.checked }),
        Cell: CheckboxCell,
        Filter: CheckboxFilter,
      },
      {
        header: 'Alert Segment',
        accessorFn: ({ buildings, equipment }) =>
          equipment ? equipment.map((e) => e?.name ?? e) : buildings?.map(({ name }) => name),
        enableEditing: false,
        Cell: AlertSegmentList,
      },
      {
        header: 'Alert Interval',
        accessorFn: ({ alert_initial_interval: interval }) => interval / 60,
        filterVariant: 'range',
        enableEditing: true,
        Cell: DurationCell,
        // eslint-disable-next-line react/no-unstable-nested-components
        Edit: ({ row }) => (
          <NumberEditCell
            label="minutes"
            value={row.original.alert_initial_interval / 60}
            validator={(newValue) =>
              newValue < 60 ? 'Value must be at least 60 minutes' : undefined
            }
            update={(newValue) =>
              updateAlert(row.original.id, { alert_initial_interval: newValue * 60 })
            }
          />
        ),
      },
      {
        header: 'Re-Alerting Interval',
        accessorFn: ({ alert_realert_interval: interval }) => interval / 60,
        enableEditing: true,
        filterVariant: 'range',
        Cell: DurationCell,
        // eslint-disable-next-line react/no-unstable-nested-components
        Edit: ({ row }) => (
          <NumberEditCell
            label="minutes"
            value={row.original.alert_realert_interval / 60}
            validator={(newValue) =>
              newValue && newValue < 60 ? 'Value must be at least 60 minutes' : undefined
            }
            update={(newValue) =>
              updateAlert(row.original.id, { alert_realert_interval: newValue * 60 })
            }
          />
        ),
      },
      {
        header: 'Alert Cleared Interval',
        accessorFn: ({ alert_clear_interval: interval }) => interval / 60,
        enableEditing: true,
        filterVariant: 'range',
        Cell: DurationCell,
        // eslint-disable-next-line react/no-unstable-nested-components
        Edit: ({ row, renderedCellValue }) => (
          <NumberEditCell
            label="minutes"
            value={renderedCellValue}
            validator={(newValue) =>
              newValue && newValue < 60 ? 'Value must be at least 60 minutes' : undefined
            }
            update={(newValue) =>
              updateAlert(row.original.id, { alert_clear_interval: newValue * 60 })
            }
          />
        ),
      },
      {
        header: 'Alert Status',
        accessorKey: 'fire_state',
        enableEditing: false,
        filterVariant: 'multi-select',
        Cell: AlertLinkCell,
      },
      {
        header: 'Alert Triggered',
        accessorKey: 'last_fired',
        enableEditing: false,
        enableColumnFilter: false,
        Cell: DateCell,
      },
      {
        header: 'Emails',
        accessorKey: 'alert_emails',
        enableEditing: true,
        Cell: CellList,
        // eslint-disable-next-line react/no-unstable-nested-components
        Edit: ({ row }) => (
          <ListTextEditCell
            value={row.original.alert_emails}
            validator={(e) => e === '' || validateEmails([e])}
            update={(newValue) =>
              updateAlert(row.original.id, { alert_emails: unique(newValue.filter(Boolean)) })
            }
          />
        ),
      },
      {
        header: 'Thresholds',
        accessorFn: ({ percent_threshold: percentThreshold }) =>
          Math.round(percentThreshold * 100 * 100) / 100,
        enableEditing: true,
        filterVariant: 'range',
        Cell: ({ renderedCellValue }) => `${renderedCellValue}%`,
        // eslint-disable-next-line react/no-unstable-nested-components
        Edit: ({ row }) => (
          <NumberEditCell
            label="percent"
            value={Math.round(row.original.percent_threshold * 100 * 100) / 100}
            validator={(v) => (v > 100 || v <= 0 ? 'Value must be between 0 and 100%' : undefined)}
            update={(newValue) =>
              updateAlert(row.original.id, { percent_threshold: newValue / 100 })
            }
          />
        ),
      },
    ],
    [updateAlert]
  );
  const data = useMemo(
    () =>
      alerts.map((a) => ({
        ...a,
        equipment: a.equipment?.map((eid) => {
          const e = equipmentById[eid];
          if (!e) return eid;
          const building = buildingsById[e.building_id];
          return { building, ...e };
        }),
        buildings: a.buildings?.map((bid) => buildingsById[bid]).filter(Boolean),
      })),
    [alerts, buildingsById, equipmentById]
  );

  const openDeleteConfirmModal = (row) => {
    confirm({
      title: `Delete Alert`,
      description: `Are you sure you want to delete this alert?`,
      confirmationText: 'Delete Alert',
    }).then(({ confirmed }) => {
      if (confirmed) {
        removeAlert(row.original.id);
      }
    });
  };

  const mrt = useMaterialReactTable({
    ...defaultTableOptions,
    ...tableCellularEditingOptions,
    enableGlobalFilter: false,
    columns,
    data,
    initialState: {
      ...defaultTableInitialState({
        columnFilters,
        sorting,
        hiddenColumns: ['alert_emails'],
      }),
    },
    renderEmptyRowsFallback: ({ table }) => (
      <span style={{ textAlign: 'center', padding: '1rem' }}>
        {table.getPreFilteredRowModel().rows.length === 0
          ? 'No alerts here, create one on the Buildings Page.'
          : 'No alerts visible with current filters.'}
      </span>
    ),
    getRowId: (d) => d.id,
    // sorting
    onSortingChange,
    // filtering
    onColumnFiltersChange,
    enableEditing: true,
    enableRowActions: true,
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: '1rem' }}>
        <Tooltip title="Delete">
          <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
            <Delete />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    state: {
      sorting,
      columnFilters,
      isLoading: !alertsLoaded,
    },
  });

  return (
    <>
      <DataAlertsLoader />
      <BuildingsLoader />
      <EquipmentLoader org={orgId} getPoints={false} getDevices={false} />
      <MaterialReactTable table={mrt} />
    </>
  );
};

const mapStateToProps = (state) => {
  const {
    entities: {
      dataAlerts: { alerts, loaded },
      buildings: { byId: buildingsById },
      equipment: { byId: equipmentById },
    },
    user: { orgId },
  } = state;
  return {
    alerts,
    alertsLoaded: loaded,
    buildingsById,
    equipmentById,
    orgId,
  };
};

const mapDispatchToProps = (dispatch) => ({
  removeAlert: (alertId) => dispatch(removeAlertAC(alertId)),
  updateAlert: (alertId, changes) => dispatch(updateAlertAC(alertId, changes)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DataAlerts);
